import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { useEffect } from 'react'
import { Path, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import {
    Contract,
    ProductConfigOption,
    ServiceConfigOption,
} from '@/modules/Contract/types'
import useServicePriceTotal from '@/hooks/useServicePriceTotal'
import Component from '../../../Config'
import { PRICING_MODELS, UNLIMITED } from '@/modules/Contract/constants'
import { WAREHOUSE_SCOPE } from '../../../Config/Warehouse/constants'

interface ConfigurationProps {
    product_config_options: ProductConfigOption[]
    index: number
    isPOC?: boolean
}

const Configuration: React.FC<ConfigurationProps> = ({
    product_config_options,
    index,
    isPOC = false,
}) => {
    const { t } = useTranslation()
    const { setValue, control } = useFormContext<Contract>()

    const key = `order.products.[${index}].service_configoptions`

    const { fields, remove } = useFieldArray({
        control,
        name: key as any,
    })

    const service_configoptions = useWatch({
        control,
        name: key as Path<Contract>,
    }) as ServiceConfigOption[]

    const product_form = useWatch({
        control,
        name: `order.products.[${index}].product_form` as Path<Contract>,
    })

    useServicePriceTotal({
        control,
        index,
        service_configoptions,
        setValue,
        servicePriceModel: PRICING_MODELS.Package,
    })

    useEffect(() => {
        const options = (product_config_options || [])
            ?.map((option) => {
                const oldOption = service_configoptions?.find(
                    (s) => s.name === option.name
                )

                return {
                    name: option.name,
                    unit: option.unit,
                    type: option.type,
                    title: option.title,
                    prices: option.product_pricing_steps?.map(
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        ({ product_pricing_step_id, ...rest }) => ({
                            ...rest,
                        })
                    ),
                    price_config: {
                        method: 'progressive',
                    },
                    commitment_prepaid: true,
                    statementable: false,
                    commitment_price_per_unit: option?.price || undefined,
                    ...(oldOption || {}),
                    commitment_value: option.value || undefined,
                    commitment_time: product_form === 'commit' ? 1 : 0,
                    resizable: option?.is_editable,
                    product_form: 'not_commit',
                    max: option?.max || UNLIMITED,
                    scope: WAREHOUSE_SCOPE.DEFAULT,
                }
            })
            ?.sort((a, b) => {
                // Put server-warehouse type first
                if (
                    a.type === 'server-warehouse' &&
                    b.type !== 'server-warehouse'
                ) {
                    return -1
                }
                if (
                    b.type === 'server-warehouse' &&
                    a.type !== 'server-warehouse'
                ) {
                    return 1
                }
                // Keep original order for other types
                return 0
            })

        setValue(key as any, options)
    }, [product_form, product_config_options])

    return (
        <div className="w-full">
            <Typogaphy size="medium" className="font-medium">
                {t('configuration-information')}
            </Typogaphy>

            <div className="mt-4 w-full">
                {fields?.map((option, i) => {
                    // @ts-ignore
                    const Comp = Component[option.type]
                    if (!Comp) return null

                    return (
                        <Comp
                            key={option.id}
                            index={i}
                            name={key}
                            productIndex={index}
                            remove={remove}
                            isPOC={isPOC}
                        />
                    )
                })}
            </div>
        </div>
    )
}

export default Configuration
